import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format } from 'date-fns';
import frLocale from 'date-fns/locale/fr';
import 'chartjs-adapter-date-fns';

import './styles/MetalsLiveChart.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, TimeScale, Tooltip, Legend, Filler);

const METAL_CONFIG = {
  XAU: {
    label: 'Or',
    accent: '#ffb347',
    accentDark: '#ff8c00',
    gradientStart: 'rgba(255, 179, 71, 0.28)',
    gradientEnd: 'rgba(255, 179, 71, 0.05)',
    currency: 'EUR'
  },
  XAG: {
    label: 'Argent',
    accent: '#c0d3ff',
    accentDark: '#7f9cf5',
    gradientStart: 'rgba(137, 164, 255, 0.25)',
    gradientEnd: 'rgba(137, 164, 255, 0.05)',
    currency: 'EUR'
  },
  XPT: {
    label: 'Platine',
    accent: '#b8ffd6',
    accentDark: '#2fc484',
    gradientStart: 'rgba(47, 196, 132, 0.24)',
    gradientEnd: 'rgba(47, 196, 132, 0.05)',
    currency: 'EUR'
  },
  XPD: {
    label: 'Palladium',
    accent: '#ff9cd9',
    accentDark: '#f24fa4',
    gradientStart: 'rgba(242, 79, 164, 0.22)',
    gradientEnd: 'rgba(242, 79, 164, 0.05)',
    currency: 'EUR'
  }
};

const PERIOD_OPTIONS = [
  { value: '1M', label: '1 mois' },
  { value: '3M', label: '3 mois' },
  { value: '6M', label: '6 mois' },
  { value: '1Y', label: '1 an' },
  { value: '5Y', label: '5 ans' },
  { value: 'MAX', label: 'Max' }
];

const priceFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2
});

const percentFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const MetalsLiveChart = () => {
  const [symbol, setSymbol] = useState('XAU');
  const [period, setPeriod] = useState('1Y');
  const [history, setHistory] = useState([]);
  const [range, setRange] = useState(null);
  const [latest, setLatest] = useState(null);
  const [fixing, setFixing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastFetched, setLastFetched] = useState(null);

  const metalDisplay = METAL_CONFIG[symbol];

const formatPrice = (value, { withUnit = true } = {}) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  const formatted = priceFormatter.format(value);
  return withUnit ? `${formatted} / kg` : formatted;
};

  const formatDatetime = (value) => {
    if (!value) return '—';
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return format(parsed, "dd MMM yyyy • HH:mm:ss", { locale: frLocale });
  };

  const determineTimeUnit = useMemo(() => {
    switch (period) {
      case '1M':
      case '3M':
        return 'day';
      case '6M':
      case '1Y':
        return 'week';
      case '5Y':
        return 'month';
      default:
        return 'month';
    }
  }, [period]);

  const fetchData = useCallback(async (options = { silent: false }) => {
    const { silent } = options;
    if (!silent) {
      setLoading(true);
    }
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/metals/live-dashboard', {
        params: {
          symbol,
          period
        },
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined
        }
      });

      setHistory(response.data.history || []);
      setRange(response.data.range || null);
      setLatest(response.data.latest || null);
      setFixing(response.data.fixing || null);
      setLastFetched(new Date());
    } catch (err) {
      console.error(err);
      setError("Impossible de charger les données des métaux");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [period, symbol]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchData({ silent: true });
    }, 15000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchData]);

  const chartData = useMemo(() => {
    const points = (history || [])
      .filter((entry) => entry.price !== null && entry.price !== undefined)
      .map((entry) => ({
        x: entry.date,
        y: Number(entry.price)
      }));

    return {
      datasets: [
        {
          label: `${metalDisplay.label} (${symbol}) - €/kg`,
          data: points,
          fill: true,
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.3,
          borderColor: metalDisplay.accentDark,
          pointHoverRadius: 5,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: metalDisplay.accentDark,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
              return metalDisplay.gradientStart;
            }
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, metalDisplay.gradientStart);
            gradient.addColorStop(1, metalDisplay.gradientEnd);
            return gradient;
          }
        }
      ]
    };
  }, [history, metalDisplay.accentDark, metalDisplay.gradientEnd, metalDisplay.gradientStart, metalDisplay.label, symbol]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: '#101522',
        borderColor: '#1d2b4f',
        borderWidth: 1,
        padding: 12,
        titleColor: '#f8fafc',
        bodyColor: '#cbd5f5',
        callbacks: {
          label: (context) => formatPrice(context.parsed.y),
          title: (items) => {
            if (!items.length) return '';
            const { parsed } = items[0];
            const date = new Date(parsed.x);
            return format(date, "dd MMM yyyy", { locale: frLocale });
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: determineTimeUnit,
          displayFormats: {
            day: 'dd MMM',
            week: "dd MMM",
            month: "MMM yyyy"
          }
        },
        ticks: {
          color: '#9aa6d7',
          maxRotation: 0,
          autoSkipPadding: 20
        },
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          color: '#9aa6d7',
          callback: (value) => formatPrice(value)
        },
        grid: {
          color: 'rgba(99, 112, 169, 0.15)'
        }
      }
    }
  }), [determineTimeUnit]);

  const changeClass = useMemo(() => {
    if (!latest || latest.change === null || latest.change === undefined) {
      return '';
    }
    if (latest.change > 0) return 'is-up';
    if (latest.change < 0) return 'is-down';
    return '';
  }, [latest]);

  const changePercentValue = latest && latest.changePercent !== null && latest.changePercent !== undefined
    ? latest.changePercent
    : null;

  return (
    <div className="metals-live">
      <div className="metals-live__header">
        <div className="metals-live__titles">
          <span className="metals-live__eyebrow">Cours des métaux précieux</span>
          <h1 className="metals-live__title">Vision premium & temps réel</h1>
        </div>

        <div className="metals-live__symbols">
          {Object.entries(METAL_CONFIG).map(([code, config]) => (
            <button
              key={code}
              type="button"
              className={`metals-live__symbol ${code === symbol ? 'is-active' : ''}`}
              onClick={() => setSymbol(code)}
            >
              <span className="metals-live__symbol-code">{code}</span>
              <span className="metals-live__symbol-label">{config.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="metals-live__content">
        <div className="metals-live__main">
          <div className="metals-live__topbar">
            <div className="metals-live__price-card">
              <span className="metals-live__price-label">Dernier prix (€/kg)</span>
              <span className="metals-live__price-value" style={{ color: metalDisplay.accentDark }}>
                {formatPrice(latest?.price)}
              </span>
              <span className="metals-live__price-sub">
                Daté du {formatDatetime(latest?.timestamp)}
              </span>
            </div>

            <div className={`metals-live__change ${changeClass}`}>
              <span className="metals-live__change-label">Variation (€/kg)</span>
              <span className="metals-live__change-value">{formatPrice(latest?.change)}</span>
              <span className="metals-live__change-pct">
                {changePercentValue !== null
                  ? percentFormatter.format(changePercentValue / 100)
                  : '—'}
              </span>
            </div>

            <div className="metals-live__periods">
              {PERIOD_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`metals-live__period ${period === option.value ? 'is-active' : ''}`}
                  onClick={() => setPeriod(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="metals-live__chart-card">
            {loading ? (
              <div className="metals-live__loader">
                <div className="metals-live__spinner" />
                <span>Chargement des données marché…</span>
              </div>
            ) : error ? (
              <div className="metals-live__error">{error}</div>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        <aside className="metals-live__aside">
          <div className="metals-live__aside-card">
            <h2 className="metals-live__aside-title">Détails marché</h2>
            <dl className="metals-live__details">
              <div>
                <dt>Prix précédent</dt>
                <dd>{formatPrice(latest?.previousClose)}</dd>
              </div>
              <div>
                <dt>Bid / Ask</dt>
                <dd>
                  {formatPrice(latest?.bid)}
                  <span className="metals-live__separator">/</span>
                  {formatPrice(latest?.ask)}
                </dd>
              </div>
              <div>
                <dt>Spread</dt>
                <dd>{formatPrice(latest?.spread)}</dd>
              </div>
              <div>
                <dt>Points historiques</dt>
                <dd>{range?.effectivePoints ?? range?.points ?? '—'}</dd>
              </div>
            </dl>
          </div>

          <div className="metals-live__aside-card">
            <h2 className="metals-live__aside-title">London Fixing</h2>
            <div className="metals-live__fixing">
              <span className="metals-live__fixing-price">{formatPrice(fixing?.price)}</span>
              <span className="metals-live__fixing-date">{formatDatetime(fixing?.timestamp)}</span>
            </div>
          </div>

          <div className="metals-live__aside-card">
            <h2 className="metals-live__aside-title">Actualisation</h2>
            <p className="metals-live__refresh">
              Flux live rafraîchi toutes les 15&nbsp;secondes.<br />
              Dernière synchro&nbsp;: {lastFetched ? format(lastFetched, "HH:mm:ss", { locale: frLocale }) : '—'}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default MetalsLiveChart;

